import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import visaImage from "../../assets/v.jpeg";
import {Link} from "react-router-dom"

/* ================= ANIMATION VARIANTS ================= */

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.25,
    },
  },
};

const itemVariants = {
  hidden: {
    opacity: 0,
    y: 50,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.9,
      ease: [0.22, 1, 0.36, 1], // WordPress easing
    },
  },
};

const imageVariants = {
  hidden: {
    opacity: 0,
    x: 60,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 1,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

/* ================= COMPONENT ================= */

export default function VisaJourney() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-120px" });

  return (
    <section ref={ref} className="w-full bg-white overflow-hidden">
      <div className="max-w-[1300px] mx-auto px-6 md:px-10 py-16 md:py-20 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-6 items-center">

        {/* IMAGE – MOBILE (TOP) */}
        <motion.div
          variants={itemVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="flex justify-center lg:hidden mb-6"
        >
          <img
            src={visaImage}
            alt="Visa Student"
            className="w-[260px] sm:w-[300px] object-contain"
          />
        </motion.div>

        {/* LEFT CONTENT */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="max-w-[680px]"
        >
          <motion.p
            variants={itemVariants}
            className="text-[13px] font-semibold tracking-widest text-blue-600 mb-3"
          >
            YOUR TRAVEL MADE EASY
          </motion.p>

          <motion.h1
            variants={itemVariants}
            className="text-[32px] md:text-[42px] font-extrabold leading-[1.15] text-gray-900 mb-4"
          >
            SMOOTH VISA JOURNEY <br />
            <span className="text-blue-600">GUARANTEED</span>
          </motion.h1>

          <motion.p
            variants={itemVariants}
            className="text-[15px] text-gray-600 leading-7 mb-6"
          >
            We provide expert guidance for every visa application, ensuring smooth
            processing, personalized support, and reliable assistance.
          </motion.p>

          {/* FEATURES */}
          <div className="space-y-4 mb-8">
            {["Expert Consultants", "Personalized Support"].map((title) => (
              <motion.div
                key={title}
                variants={itemVariants}
                className="flex items-start gap-4"
              >
                <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center shrink-0">
                  <div className="w-3.5 h-3.5 bg-white rounded-full" />
                </div>

                <div>
                  <h4 className="text-[18px] font-bold text-gray-900 mb-0.5">
                    {title}
                  </h4>
                  <p className="text-[14px] text-gray-600 leading-6">
                    Skilled and knowledgeable visa advisors offering reliable
                    assistance.
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* BUTTON */}
          <Link to="/contact">
          <motion.button
            variants={itemVariants}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="group flex items-center gap-4 border border-blue-600 rounded-full px-7 py-3 text-[14px] font-semibold bg-blue-600 text-white hover:bg-black hover:border-black transition-all duration-300"
          >
            GET STARTED TODAY
            <span className="w-9 h-9 flex items-center justify-center rounded-full bg-white text-blue-600 group-hover:text-black transition-all">
              →
            </span>
          </motion.button>
          </Link>
        </motion.div>

        {/* RIGHT IMAGE – DESKTOP */}
        <motion.div
          variants={imageVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="hidden lg:flex justify-end -ml-16"
        >
          <img
            src={visaImage}
            alt="Visa Student"
            className="w-[400px] xl:w-[460px] object-contain"
          />
        </motion.div>

      </div>
    </section>
  );
}
